/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.20.0
 */

import { useEffect, useRef, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import { inquiryApi } from '@/main/modules/inquiry/inquiryApi';
import { convertBytesToReadableFormat } from '@/main/utils/convertBytesToReadableFormat';
import { getFileExtension } from '@/main/utils/formatText';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { ImgStyled } from '@components/common/Block/Img';
import EllipsisText from '@components/common/EllipsisText/EllipsisText';
import { FormBlockStyled } from '@components/common/FormStyled/FormStyled';
import { LinkBlock } from '@components/common/index';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import EFormValidation from '@modules/form/const/formValidation';
import { ECreateInquiryField as F, ECreateInquiryType } from '@modules/inquiry/constants';
import type {
    TDeleteFileError,
    FileId,
    ICreateInquiryForm,
    TFileError,
    ISortedRejectedFiles,
} from '@modules/inquiry/types';
import { isFailedFileAttach, isSuccessFileAttach } from '@modules/inquiry/util/validation';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import {
    makeSelectAuthSystemFileUploadLimits,
} from '@pages/PageAuth/selectors/authSelectors';
import { getGlobalInputErrorModalTitle } from '@pages/PageInquiryInfo/utils/getGlobalInputErrorTitle';
import { fontSizeSmall } from '@theme/default/global';

import { sortRejectedFiles } from '../../util/sortRejectedFiles';
import CreateInquiryFieldLabel from '../CreateInquiryFieldLabel';
import CreateInquiryFileErrorItemsContainer from '../CreateInquiryFileErrorItemsContainer';
import {
    InquiryFileItemStyled,
    InquiryFileItemsContainerStyled,
    InquiryFileInputBlockStyled,
    InquiryFileInputGlobalErrorBlockStyled,
} from '../styled/CreateInquiryFileAttributeStyled';

interface ICreateInquiryFileAttributeProps {
    required: boolean;
    type: ECreateInquiryType;
    allowedFileExtensions?: string[];
    maxFileCount?: number;
}

const CreateInquiryFileAttribute = ({
    required,
    type,
    allowedFileExtensions,
    maxFileCount,
}: ICreateInquiryFileAttributeProps) => {
    const [sortedRejectedFiles, setSortedRejectedFiles] = useState<ISortedRejectedFiles | null>(null);

    const [scrollToField, setScrollToField] = useState(false);

    const { setFieldValue, getFieldProps } = useFormikContext<ICreateInquiryForm>();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const { form: tForm, error: TError } = tCommon;

    const fileLimits = useSelector(makeSelectAuthSystemFileUploadLimits());

    const [attachFile, { isLoading: loadingAttach }] = inquiryApi.endpoints.attachFile.useMutation();
    const [deleteFile] = inquiryApi.endpoints.deleteFile.useMutation();

    const { value: attachedFiles } = getFieldProps<Map<FileId, File>>(F.file);
    const { value: topic } = getFieldProps<string>(F.topic);

    const { current: rejectedFiles } = useRef(new Map<string, TFileError>());
    const [deleteErrors, setDeleteErrors] = useState<TDeleteFileError[]>([]);
    const [globalInputError, setGlobalInputError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLDivElement | null>(null);
    const attachedFilesLengthRef = useRef(0);

    useEffect(() => {
        rejectedFiles.clear();
    }, [rejectedFiles, topic]);

    useEffect(() => {
        if (fileInputRef.current && scrollToField) {
            fileInputRef.current.scrollIntoView();
            setScrollToField(false);
        }
    }, [scrollToField]);

    const onDropHandler = (acceptedFiles: File[]) => {
        rejectedFiles.clear();
        setGlobalInputError(null);
        const maxFileLength = maxFileCount ? maxFileCount : 1;

        if (acceptedFiles) {
            if (acceptedFiles.length <= maxFileLength - attachedFilesLengthRef.current) {
                acceptedFiles.forEach((file, index) => {
                    const attachedNames = Array.from(attachedFiles, (fileData) => fileData[1].name);

                    if (
                        allowedFileExtensions?.length &&
                        !allowedFileExtensions.includes(getFileExtension(file.name).toUpperCase())
                    ) {
                        rejectedFiles.set(file.name, { file, error: EFormValidation.fileFormatIncorrect });
                    } else if (file.size > fileLimits.maxFileSize) {
                        rejectedFiles.set(file.name, { file, error: EFormValidation.fileSizeExceeded });
                    } else if (file.name.length > fileLimits?.maxFileNameLength) {
                        rejectedFiles.set(file.name, { file, error: EFormValidation.fileNameTooLong });
                    } else if (attachedNames.includes(file.name)) {
                        rejectedFiles.set(file.name, { file, error: EFormValidation.fileExist });
                    } else {
                        attachedFilesLengthRef.current++;
                        attachFile({ file, type }).then((item) => {
                            if (isSuccessFileAttach(item)) {
                                attachedFiles.set(item.data.fileId, acceptedFiles[index]);
                                setFieldValue(F.file, attachedFiles);
                            }
                            if (isFailedFileAttach(item)) {
                                rejectedFiles.set(acceptedFiles[index].name, {
                                    file: acceptedFiles[index],
                                    error: item.error.code as TFileError['error'],
                                });
                                setSortedRejectedFiles(sortRejectedFiles(rejectedFiles));
                                GTMOperations.navigation.inquiryErrorCreateOperation(JSON.stringify(item.error || {}));
                            }
                        });
                    }
                });
                setSortedRejectedFiles(sortRejectedFiles(rejectedFiles));
                setScrollToField(true);
                GTMOperations.navigation.inquiryAddFileClickOperation();
            } else {
                setGlobalInputError(
                    getGlobalInputErrorModalTitle(
                        tForm,
                        acceptedFiles.length + attachedFilesLengthRef.current,
                        maxFileLength,
                    ),
                );
            }
        }
    };

    const onDeleteFile = (fileId: FileId) => {
        deleteFile({ fileId })
            .unwrap()
            .then(() => {
                attachedFilesLengthRef.current--;
                attachedFiles.delete(fileId);
            })
            .catch((error) => {
                const deleteError = {
                    fileId,
                    message: error.code ? TError.codes[error.code] : tForm.validation[EFormValidation.fileDeleteFailed],
                };

                setDeleteErrors([...deleteErrors, deleteError]);
            })
            .finally(() => {
                setFieldValue(F.file, attachedFiles);
                rejectedFiles.clear();
            });
    };

    const { getInputProps, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: true,
        noClick: false,
        noKeyboard: false,
    });

    const attachedFilesItems = Array.from(attachedFiles.entries()).map(([fileId, file]) => {
        const deleteError = deleteErrors.find((errorItem) => errorItem.fileId === fileId);

        const deleteErrorMessage = deleteError ? deleteError.message : '';

        return (
            <InquiryFileItemStyled
                className={deleteError && deleteError.fileId === fileId ? 'failed' : ''}
                key={fileId}
            >
                <EllipsisText title={file.name} tooltipMsg={deleteErrorMessage}>
                    {file.name}
                </EllipsisText>
                <ImgStyled
                    className="small pointer"
                    src={icons.cancelMiddleIcon}
                    onClick={() => onDeleteFile(fileId)}
                />
            </InquiryFileItemStyled>
        );
    });

    return (
        <FormBlockStyled ref={fileInputRef}>
            {/* Hidden input */}
            <input {...getInputProps()} />
            {globalInputError && (
                <InquiryFileInputGlobalErrorBlockStyled>{globalInputError}</InquiryFileInputGlobalErrorBlockStyled>
            )}
            <InquiryFileInputBlockStyled>
                <LinkBlock dataName="inquiry-field-file-attachment" onClick={open}>
                    {tForm.attachFile}
                    {required && <LabelMarkStyled>*</LabelMarkStyled>}
                </LinkBlock>
                {!!maxFileCount && (
                    <Typography component="span" fontSize={fontSizeSmall} themeColor="grey">
                        {stringFormatParams(tForm.maxFilesLength, {
                            count: maxFileCount,
                        })}
                    </Typography>
                )}
            </InquiryFileInputBlockStyled>
            <Loader showLoader={loadingAttach}>
                {!!attachedFiles.size && (
                    <InquiryFileItemsContainerStyled>
                        <CreateInquiryFieldLabel name={tForm.attachedFiles} />
                        {attachedFilesItems}
                    </InquiryFileItemsContainerStyled>
                )}
                {!!sortedRejectedFiles?.rejectedFilesMaxSizesItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesMaxSizesItems}
                        name={stringFormatParams(tForm.validation[EFormValidation.filesSizeExceeded], {
                            sizeLimit: convertBytesToReadableFormat(fileLimits.maxFileSize, tCommon),
                        })}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesEmptyDataItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesEmptyDataItems}
                        name={tForm.validation[EFormValidation.fileDataEmpty]}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesNameTooLongItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesNameTooLongItems}
                        name={stringFormatParams(tForm.validation[EFormValidation.fileNameTooLong], {
                            nameLimit: fileLimits.maxFileNameLength,
                        })}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesExistsItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesExistsItems}
                        name={tForm.validation[EFormValidation.fileExist]}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesUploadUnavailableItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesUploadUnavailableItems}
                        name={tForm.validation[EFormValidation.fileUploadUnavailable]}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesUnknownErrorItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesUnknownErrorItems}
                        name={tForm.validation[EFormValidation.fileNotFound]}
                    />
                )}
                {!!sortedRejectedFiles?.rejectedFilesFormatIncorrectItems.length && (
                    <CreateInquiryFileErrorItemsContainer
                        rejected
                        files={sortedRejectedFiles.rejectedFilesFormatIncorrectItems}
                        name={stringFormatParams(tForm.validation[EFormValidation.fileFormatIncorrect], {
                            format: allowedFileExtensions?.join(', ').toUpperCase(),
                        })}
                    />
                )}
            </Loader>
        </FormBlockStyled>
    );
};

export default CreateInquiryFileAttribute;
